import { gql } from "@apollo/client";

const AUDIENCE_CONTACT_FIELD_FIELDS = gql`
  fragment AudienceContactFieldFields on AudienceContactField {
    id
    name
    displayName
    type
    typeParameters {
      maxLength
      values
    }
    isEditable
    createdAt
    updatedAt
  }
`;

export const GET_AUDIENCE_CONTACT_FIELDS = gql`
  ${AUDIENCE_CONTACT_FIELD_FIELDS}
  query getAudienceContactFields($input: GetAudienceContactFieldsInput!) {
    getAudienceContactFields(input: $input) {
      ...AudienceContactFieldFields
    }
  }
`;

export const DELETE_AUDIENCE_CONTACT_FIELD = gql`
  mutation deleteAudienceContactField(
    $input: DeleteAudienceContactFieldInput!
  ) {
    deleteAudienceContactField(input: $input) {
      success
    }
  }
`;

export const CREATE_CONTACT_FIELD = gql`
  ${AUDIENCE_CONTACT_FIELD_FIELDS}
  mutation createAudienceContactField(
    $input: CreateAudienceContactFieldInput!
  ) {
    createAudienceContactField(input: $input) {
      ...AudienceContactFieldFields
    }
  }
`;

export const UPDATE_CONTACT_FIELD = gql`
  ${AUDIENCE_CONTACT_FIELD_FIELDS}
  mutation updateAudienceContactField(
    $input: UpdateAudienceContactFieldInput!
  ) {
    updateAudienceContactField(input: $input) {
      ...AudienceContactFieldFields
    }
  }
`;

export const ENQUEUE_AUDIENCE_CONTACT_SEARCH = gql`
  mutation enqueueAudienceContactSearch(
    $input: EnqueueAudienceContactSearchInput!
  ) {
    enqueueAudienceContactSearch(input: $input) {
      success
    }
  }
`;

const AUDIENCE_SEARCH_FILTER_FIELDS = gql`
  fragment AudienceSearchFilterFields on AudienceSearchFilter {
    id
    subaccountId
    tenantId
    audienceId
    name
    type
    searchBody {
      targetGroups {
        clauses {
          fieldName
          fieldType
          filterCategory
          filterConfig {
            centerZips
            radius
          }
          filterOperation
          value
          values
        }
        dateFilter {
          endDate
          key
          startDate
        }
      }
    }
    searchJob {
      status
      result {
        contactCount
        optedOutContactCount
        calculatedAt
      }
    }
    createdAt
    createdByUserId
    createdBy {
      id
      fullName
    }
    updatedAt
    deletedAt
  }
`;

const AUDIENCE_SEARCH_RESULT_FIELDS = gql`
  fragment AudienceSearchResultFields on AudienceSearchResult {
    id
    subaccountId
    tenantId
    audienceId
    audienceContactId
    audienceSearchFilterId
    createdAt
    updatedAt
    phoneNumber
    fields {
      name
      displayName
      type
      value
    }
    optedIn
  }
`;

export const GET_AUDIENCE_SEARCH_USER_FILTER = gql`
  ${AUDIENCE_SEARCH_FILTER_FIELDS}
  query getAudienceSearchUserFilter($input: GetAudienceSearchUserFilterInput!) {
    getAudienceSearchUserFilter(input: $input) {
      ...AudienceSearchFilterFields
    }
  }
`;

export const GET_AUDIENCE_SEARCH_FILTER = gql`
  ${AUDIENCE_SEARCH_FILTER_FIELDS}
  query getAudienceSearchFilter($input: GetAudienceSearchFilterInput!) {
    getAudienceSearchFilter(input: $input) {
      ...AudienceSearchFilterFields
    }
  }
`;

export const LIST_AUDIENCE_SEARCH_FILTERS = gql`
  ${AUDIENCE_SEARCH_FILTER_FIELDS}
  query listAudienceSearchFilters($input: ListAudienceSearchFiltersInput!) {
    listAudienceSearchFilters(input: $input) {
      audienceSearchFilters {
        ...AudienceSearchFilterFields
      }
      totalCount
    }
  }
`;

export const CREATE_AUDIENCE_SEARCH_FILTER = gql`
  ${AUDIENCE_SEARCH_FILTER_FIELDS}
  mutation CreateAudienceSearchFilter(
    $input: CreateAudienceSearchFilterInput!
  ) {
    createAudienceSearchFilter(input: $input) {
      ...AudienceSearchFilterFields
    }
  }
`;

export const UPDATE_AUDIENCE_SEARCH_FILTER = gql`
  ${AUDIENCE_SEARCH_FILTER_FIELDS}
  mutation UpdateAudienceSearchFilter(
    $input: UpdateAudienceSearchFilterInput!
  ) {
    updateAudienceSearchFilter(input: $input) {
      ...AudienceSearchFilterFields
    }
  }
`;

export const DELETE_AUDIENCE_SEARCH_FILTER = gql`
  mutation DeleteAudienceSearchFilter(
    $input: DeleteAudienceSearchFilterInput!
  ) {
    deleteAudienceSearchFilter(input: $input) {
      success
    }
  }
`;

export const DELETE_AUDIENCE_SEARCH_USER_FILTER = gql`
  mutation deleteAudienceSearchUserFilter(
    $input: DeleteAudienceSearchUserFilterInput!
  ) {
    deleteAudienceSearchUserFilter(input: $input) {
      success
    }
  }
`;

export const UPSERT_AUDIENCE_SEARCH_USER_FILTER = gql`
  ${AUDIENCE_SEARCH_FILTER_FIELDS}
  mutation upsertAudienceSearchUserFilter(
    $input: UpsertAudienceSearchUserFilterInput!
  ) {
    upsertAudienceSearchUserFilter(input: $input) {
      ...AudienceSearchFilterFields
    }
  }
`;

export const LIST_AUDIENCE_SEARCH_RESULTS = gql`
  ${AUDIENCE_SEARCH_RESULT_FIELDS}
  query listAudienceSearchResults($input: ListAudienceSearchResultsInput!) {
    listAudienceSearchResults(input: $input) {
      audienceSearchResults {
        ...AudienceSearchResultFields
      }
      totalCount
    }
  }
`;

const AUDIENCE_FIELDS = gql`
  fragment AudienceFields on Audience {
    id
    type
    subaccountId
    tenantId
    createdAt
    updatedAt
    archivedAt
    name
    audienceContactImportJob {
      id
      status
      startedAt
      errorMessage
      args {
        firstNameColumn
        lastNameColumn
        phoneNumberColumn
        silentBackfill
        upload {
          id
          fileName
        }
      }
      result {
        nNewSubscribers
        nExistingSubscribersUpdated
        nOptedOutSubscribers
        nInvalid
        nDuplicates
        nInvalidRows
      }
    }
    shortCodeConfig {
      helpKeywords
      helpMessage
      optInKeywords
      optInMessage
      optOutKeywords
      optOutMessage
    }
    ownedPhoneNumbers {
      id
      phoneNumber
      state
      messageClass
      createdAt
      displayName
    }
    defaultSendingNumber {
      id
    }
    exportJob {
      id
      type
      progress
      status
      errorMessage
      createdAt
      updatedAt
      args {
        requestedBy {
          id
          fullName
        }
      }
      result {
        ok
      }
    }
  }
`;

export const GET_AUDIENCE = gql`
  ${AUDIENCE_FIELDS}
  query getAudience($input: GetAudienceInput!) {
    getAudience(input: $input) {
      ...AudienceFields
      ownedPhoneNumbers {
        subaccounts {
          subaccountId
          state
        }
      }
    }
  }
`;

export const EXPORT_AUDIENCE = gql`
  ${AUDIENCE_FIELDS}
  mutation exportAudience($input: ExportAudienceInput!) {
    exportAudience(input: $input) {
      ...AudienceFields
    }
  }
`;

export const LIST_AUDIENCES_FOR_SUBACCOUNT = gql`
  ${AUDIENCE_FIELDS}
  query listAudiencesForSubaccount($input: ListAudiencesForSubaccountInput!) {
    listAudiencesForSubaccount(input: $input) {
      ...AudienceFields
    }
  }
`;

export const GET_AUDIENCE_EXPORT_SIGNED_DOWNLOAD_URL = gql`
  mutation getAudienceExportSignedDownloadUrl(
    $input: GetAudienceExportSignedDownloadUrlInput!
  ) {
    getAudienceExportSignedDownloadUrl(input: $input)
  }
`;

export const GET_AUDIENCE_SUMMARY = gql`
  query getAudienceSummary($input: GetAudienceSummaryInput!) {
    getAudienceSummary(input: $input) {
      activeContacts
      optedOutContacts
    }
  }
`;

export const CREATE_NON_OPT_IN_AUDIENCE = gql`
  mutation createNonOptInAudience($input: CreateNonOptInAudienceInput!) {
    createNonOptInAudience(input: $input) {
      id
    }
  }
`;

export const UPDATE_NON_OPT_IN_AUDIENCE = gql`
  mutation updateNonOptInAudience($input: UpdateNonOptInAudienceInput!) {
    updateNonOptInAudience(input: $input) {
      name
    }
  }
`;

export const CREATE_AUDIENCE_CONTACT = gql`
  mutation createAudienceContact($input: CreateAudienceContactInput!) {
    createAudienceContact(input: $input) {
      id
      phoneNumber
      createdAt
      updatedAt
      fields {
        name
        type
        displayName
        value
      }
    }
  }
`;

export const UPDATE_AUDIENCE_CONTACT = gql`
  mutation updateAudienceContact($input: UpdateAudienceContactInput!) {
    updateAudienceContact(input: $input) {
      id
      phoneNumber
      createdAt
      updatedAt
      fields {
        name
        type
        displayName
        value
      }
    }
  }
`;

const AUDIENCE_CONTACT_FIELDS = gql`
  fragment AudienceContactWithFields on AudienceContact {
    id
    createdAt
    updatedAt
    phoneNumber
    fields {
      name
      displayName
      type
      value
    }
    updates {
      id
      createdAt
      source
      sourceDetails
    }
    subscriber {
      id
      createdAt
      updatedAt
      createdByUser {
        id
        fullName
      }
      phoneNumber
      fields {
        name
        displayName
        type
        value
      }
      optOuts {
        id
        createdAt
        createdByUser {
          id
          fullName
        }
        source {
          type
        }
        deletedAt
        deleteSourceType
        deletedByUser {
          id
          fullName
        }
      }
      source
    }
  }
`;

export const GET_AUDIENCE_CONTACT = gql`
  ${AUDIENCE_CONTACT_FIELDS}

  query GetAudienceContact($input: GetAudienceContactInput!) {
    getAudienceContact(input: $input) {
      ...AudienceContactWithFields
    }
  }
`;
